import { ref } from "vue";

import { fetchJson } from "~/recipal-app/controllers/shared/requests";

const DEFAULT_PER_PAGE = 10;

/* Builds a search composable over a user-scoped filter endpoint that returns its
 * rows under a single key. Every search replaces the results with the first page
 * of matches and aborts the request before it, so only the latest ones land in
 * `results` -- callers that need to page through results want their own store.
 *
 * `extraParams` maps the options a caller passes to `search` onto query params.
 */
export function createSearchStore({
  path,
  resultsKey,
  perPage = DEFAULT_PER_PAGE,
  extraParams = () => ({}),
}) {
  return function useSearch() {
    const results = ref([]);
    const loading = ref(false);
    let abortController = null;

    async function search(userId, q, options = {}) {
      abort();
      const controller = new AbortController();
      abortController = controller;

      results.value = [];
      loading.value = true;

      try {
        const params = new URLSearchParams({
          q,
          per_page: perPage,
          page: 1,
          ...extraParams(options),
        });
        const { response, data } = await fetchJson(`${path(userId)}?${params}`, {
          signal: controller.signal,
        });

        if (!response.ok || !data) {
          throw new Error(`Search failed: ${response.status}`);
        }

        results.value = data[resultsKey];
      } catch (e) {
        if (e.name !== "AbortError") throw e;
      } finally {
        if (abortController === controller) {
          abortController = null;
          loading.value = false;
        }
      }
    }

    function abort() {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    }

    function clear() {
      abort();
      loading.value = false;
      results.value = [];
    }

    return { results, loading, search, clear };
  };
}
